import React, { useEffect, useMemo } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowRight, MessageCircle, Phone, ShieldCheck } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

import { SafeImage } from "@/components/SafeImage";

import { COMPANY, IMAGES, PRODUCT_CATEGORIES, SPEC_TEMPLATES } from "@/mock";
import { Container, makeTelUrl, makeWhatsAppUrl, setTitle } from "@/pages/_shared";

export default function ProductDetailPage() {
  const { categoryId, productSlug } = useParams();
  const navigate = useNavigate();

  const { category, product } = useMemo(() => {
    const cat = PRODUCT_CATEGORIES.find((c) => c.id === categoryId);
    const prod = cat?.products?.find((p) => p.slug === productSlug);
    return { category: cat, product: prod };
  }, [categoryId, productSlug]);

  useEffect(() => {
    setTitle(product ? product.name : "Product");
  }, [product]);

  if (!category || !product) {
    return (
      <Container className="py-14">
        <div className="rounded-3xl border border-border/60 bg-background/70 p-8">
          <div className="text-lg font-extrabold">Product not found</div>
          <p className="mt-2 text-sm text-muted-foreground">Please go back to Products.</p>
          <Button className="mt-4" onClick={() => navigate("/products")}>View Products</Button>
        </div>
      </Container>
    );
  }

  const spec = product.spec || SPEC_TEMPLATES[product.template];

  return (
    <div>
      <section className="py-14">
        <Container>
          <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
            <Link className="hover:text-foreground" to="/products">Products</Link>
            <span>/</span>
            <Link className="hover:text-foreground" to={`/products/${category.id}`}>{category.name}</Link>
            <span>/</span>
            <span className="text-foreground font-semibold">{product.name}</span>
          </div>

          <div className="mt-6 grid gap-10 lg:grid-cols-12 lg:items-start">
            <div className="lg:col-span-7">
              <h1 className="text-3xl sm:text-5xl font-extrabold tracking-tight">{product.name}</h1>
              <p className="mt-4 text-base text-muted-foreground max-w-2xl">{product.desc}</p>

              <div className="mt-6 flex flex-wrap gap-2">
                <Badge className="bg-amber-100 text-amber-950 border-amber-200">Bulk order available</Badge>
                <Badge className="bg-emerald-100 text-emerald-950 border-emerald-200">{COMPANY.certification}</Badge>
                <Badge variant="secondary" className="bg-secondary/60">Typical specs shown</Badge>
              </div>

              <Separator className="my-8" />

              <h2 className="text-xl font-extrabold tracking-tight">Specifications (Typical Ranges)</h2>
              <p className="mt-2 text-sm text-muted-foreground">
                These are typical industry ranges. For exact requirements (IS/ASTM grade, diameter/mesh, coating, packaging), please request a quote.
              </p>

              <div className="mt-4 rounded-3xl border border-border/60 bg-background/70 overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Parameter</TableHead>
                      <TableHead>Typical Range</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    <TableRow><TableCell className="font-semibold">Material</TableCell><TableCell>{spec.material}</TableCell></TableRow>
                    {spec.diameter ? (
                      <TableRow><TableCell className="font-semibold">Wire Diameter / Size</TableCell><TableCell>{spec.diameter}</TableCell></TableRow>
                    ) : null}
                    {spec.size ? (
                      <TableRow><TableCell className="font-semibold">Size</TableCell><TableCell>{spec.size}</TableCell></TableRow>
                    ) : null}
                    <TableRow><TableCell className="font-semibold">Coating Type</TableCell><TableCell>{spec.coating}</TableCell></TableRow>
                    <TableRow><TableCell className="font-semibold">Tensile Strength</TableCell><TableCell>{spec.tensile}</TableCell></TableRow>
                    <TableRow><TableCell className="font-semibold">Roll / Bundle</TableCell><TableCell>{spec.bundle}</TableCell></TableRow>
                    <TableRow><TableCell className="font-semibold">Standards</TableCell><TableCell>{spec.standards}</TableCell></TableRow>
                  </TableBody>
                </Table>
              </div>

              <div className="mt-10 grid gap-8 sm:grid-cols-2">
                <div>
                  <h3 className="text-lg font-extrabold tracking-tight">Key Features</h3>
                  <ul className="mt-3 list-disc pl-5 text-sm text-muted-foreground space-y-1">
                    {product.features.map((f) => (
                      <li key={f}>{f}</li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h3 className="text-lg font-extrabold tracking-tight">Applications</h3>
                  <ul className="mt-3 list-disc pl-5 text-sm text-muted-foreground space-y-1">
                    {product.applications.map((a) => (
                      <li key={a}>{a}</li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="mt-10 rounded-3xl border border-border/60 bg-secondary/25 p-6">
                <div className="text-sm font-extrabold tracking-tight">Available Customization</div>
                <p className="mt-2 text-sm text-muted-foreground">
                  Diameter/mesh, coating type (GI/PVC), roll size, packaging, and standards confirmation—based on your BOQ and quantity.
                </p>
                <div className="mt-4 flex flex-col sm:flex-row gap-2">
                  <Button
                    className="bg-amber-500 text-slate-950 hover:bg-amber-400"
                    onClick={() => navigate("/request-quote", { state: { product: product.name } })}
                  >
                    Request Price / Bulk Order <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    className="border-emerald-200/60 bg-emerald-50 text-emerald-950 hover:bg-emerald-100"
                    onClick={() => window.open(makeWhatsAppUrl(COMPANY.whatsapp, `Hello, I need pricing for ${product.name}. Quantity: ___`), "_blank")}
                  >
                    <MessageCircle className="mr-2 h-4 w-4" /> WhatsApp Now
                  </Button>
                </div>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="overflow-hidden rounded-3xl border border-border/60 bg-background/70">
                <div className="relative aspect-[4/3]">
                  <SafeImage
                    src={product.image || category.image}
                    alt={`${product.name} image`}
                    imgClassName="h-full w-full object-cover"
                    fallbackLabel={product.name}
                  />
                  {product.imageNote ? (
                    <div className="absolute left-3 top-3">
                      <Badge className="bg-white/70 text-slate-900 border-white/70 backdrop-blur">
                        {product.imageNote}
                      </Badge>
                    </div>
                  ) : null}
                </div>
              </div>

              <div className="mt-4 overflow-hidden rounded-3xl border border-border/60 bg-background/70">
                <div className="aspect-[4/3]">
                  <SafeImage
                    src={category.id === "geotechnical-structures" ? IMAGES.gabion : IMAGES.chainlinkDetail}
                    alt="Installed application"
                    imgClassName="h-full w-full object-cover"
                    fallbackLabel="Installed application"
                  />
                </div>
              </div>

              <div className="mt-4 rounded-3xl border border-border/60 bg-slate-950 p-6 text-slate-50">
                <div className="flex items-start gap-3">
                  <div className="rounded-xl bg-white/10 border border-white/15 p-2">
                    <ShieldCheck className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="text-sm font-extrabold tracking-tight">Need compliant supply?</div>
                    <p className="mt-1 text-xs text-slate-200">
                      Share IS/ASTM standard and testing requirement—we’ll align supply accordingly.
                    </p>
                  </div>
                </div>

                <div className="mt-4 grid gap-2">
                  <Button
                    className="bg-amber-500 text-slate-950 hover:bg-amber-400"
                    onClick={() => navigate("/request-quote", { state: { product: product.name } })}
                  >
                    Request a Quote
                  </Button>
                  <Button
                    variant="outline"
                    className="border-white/25 bg-white/10 text-white hover:bg-white/15"
                    onClick={() => (window.location.href = makeTelUrl(COMPANY.phone))}
                  >
                    <Phone className="mr-2 h-4 w-4" /> Call Procurement
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </Container>
      </section>
    </div>
  );
}
